import { Routes, Route, Navigate } from "react-router-dom";
import {
  Suspense,
  lazy,
  useEffect,
  useState,
  createContext,
  useContext,
} from "react";
import { useAuth } from "./lib/auth";
import { getCurrentUser, type CurrentUser } from "./api/users";
import { Shell } from "./components/layout/shell";

function lazyNamed<TProps>(
  loader: () => Promise<Record<string, unknown>>,
  exportName: string,
) {
  return lazy(async () => {
    const mod = await loader();
    return { default: mod[exportName] as React.ComponentType<TProps> };
  });
}

const SignInPage = lazyNamed(() => import("./components/auth/sign-in"), "SignInPage");
const DashboardPage = lazyNamed(() => import("./pages/dashboard"), "DashboardPage");
const SendPage = lazyNamed(() => import("./pages/send"), "SendPage");
const RequestsPage = lazyNamed(() => import("./pages/requests"), "RequestsPage");
const RequestDetailPage = lazyNamed(() => import("./pages/request-detail"), "RequestDetailPage");
const DocumentsPage = lazyNamed(() => import("./pages/documents"), "DocumentsPage");
const PacketBuilderPage = lazyNamed(() => import("./pages/packet-builder"), "PacketBuilderPage");
const TemplatesPage = lazyNamed(() => import("./pages/templates"), "TemplatesPage");
const SettingsPage = lazyNamed(() => import("./pages/settings"), "SettingsPage");
const BillingPage = lazyNamed(() => import("./pages/billing"), "BillingPage");
const OnboardingPage = lazyNamed(() => import("./pages/onboarding"), "OnboardingPage");
const AuthCallbackPage = lazyNamed(() => import("./pages/auth-callback"), "AuthCallbackPage");

// ── User context ───────────────────────────────────────────────────────────
// Shared current-user data (including vertical) available to all app pages.

const UserContext = createContext<CurrentUser | null>(null);

export function useCurrentUser(): CurrentUser | null {
  return useContext(UserContext);
}

function FullScreenSpinner() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-400 border-t-transparent" />
    </div>
  );
}

function SignedOutRedirect() {
  useEffect(() => {
    window.location.replace("https://peekop.com");
  }, []);

  return <FullScreenSpinner />;
}

// ── Onboarding gate ────────────────────────────────────────────────────────

function ProtectedRoutes() {
  const { isSignedIn, isLoaded } = useAuth();
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [onboardingDone, setOnboardingDone] = useState<boolean | null>(null);

  useEffect(() => {
    if (!isSignedIn) {
      setUser(null);
      setOnboardingDone(true);
      return;
    }

    setOnboardingDone(null);

    getCurrentUser()
      .then((u) => {
        setUser(u);
        // Treat missing/undefined as done — only redirect when API explicitly
        // returns false. Prevents false redirects on partial API responses.
        setOnboardingDone(u.onboardingCompleted !== false);
      })
      .catch(() => setOnboardingDone(true)); // fail open
  }, [isSignedIn]);

  if (!isLoaded) return <FullScreenSpinner />;
  if (!isSignedIn) return <SignedOutRedirect />;
  if (onboardingDone === null) return <FullScreenSpinner />;
  if (!onboardingDone) return <Navigate to="/onboarding" replace />;

  return (
    <UserContext.Provider value={user}>
      <Shell>
        <Routes>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/send" element={<SendPage />} />
          <Route path="/requests" element={<RequestsPage />} />
          <Route path="/requests/:id" element={<RequestDetailPage />} />
          <Route path="/documents" element={<DocumentsPage />} />
          <Route path="/packet-builder" element={<PacketBuilderPage />} />
          <Route path="/templates" element={<TemplatesPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/billing" element={<BillingPage />} />
        </Routes>
      </Shell>
    </UserContext.Provider>
  );
}

function RootRoute() {
  const { isSignedIn, isLoaded } = useAuth();
  if (!isLoaded) return <FullScreenSpinner />;
  if (isSignedIn) return <Navigate to="/dashboard" replace />;
  return <SignedOutRedirect />;
}

export function App() {
  return (
    <Suspense fallback={<FullScreenSpinner />}>
      <Routes>
        <Route path="/" element={<RootRoute />} />
        <Route path="/sign-in/*" element={<SignInPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route path="/onboarding" element={<OnboardingRoute />} />
        <Route path="/*" element={<ProtectedRoutes />} />
      </Routes>
    </Suspense>
  );
}

function OnboardingRoute() {
  const { isSignedIn, isLoaded } = useAuth();
  if (!isLoaded) return <FullScreenSpinner />;
  if (!isSignedIn) return <SignedOutRedirect />;
  return <OnboardingPage />;
}
